import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { programContent } from '../../data/program';
import ProgramNav from '../../components/program/ProgramNav';
import AnimatedSection from '../../components/common/AnimatedSection';
import { Scale, Info } from 'lucide-react';

const ShadowCabinetPage = () => {
  const { language } = useLanguage();
  const content = programContent[language]?.shadowCabinet || programContent.en.shadowCabinet;

  return (
    <div className="pt-24">
      <ProgramNav />
      
      {/* Hero */}
      <section className="section bg-gradient-to-b from-[#FFFEF9] to-[#F5F0E8]">
        <div className="container mx-auto px-4 lg:px-10">
          <div className="max-w-4xl mx-auto text-center">
            <AnimatedSection>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#5B2D8E]/5 rounded-full mb-6">
                <Scale className="w-4 h-4 text-[#5B2D8E]" />
                <span className="text-sm font-medium text-[#5B2D8E]">Government-in-Waiting</span>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={0.1}>
              <h1 className="heading-display mb-6">{content.hero.title}</h1>
            </AnimatedSection>
            
            <AnimatedSection delay={0.2}>
              <p className="text-xl text-[#6B6B6B]" style={{ fontFamily: 'var(--font-serif)' }}>
                {content.hero.subtitle}
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-12 bg-[#FFFEF9]">
        <div className="container mx-auto px-4 lg:px-10">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection>
              <div className="bg-[#5B2D8E]/5 rounded-xl p-6 lg:p-8 border border-[#5B2D8E]/10">
                <p className="text-lg text-[#2D2D2D] leading-relaxed" style={{ fontFamily: 'var(--font-serif)' }}>
                  {content.intro}
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Ministries Table */}
      <section className="section bg-[#F5F0E8]">
        <div className="container mx-auto px-4 lg:px-10">
          <AnimatedSection className="text-center mb-12">
            <span className="caption mb-4 block">Executive Structure</span>
            <h2 className="heading-1 mb-4">Ministerial Portfolios</h2>
            <div className="divider mx-auto" />
          </AnimatedSection>

          <div className="max-w-5xl mx-auto">
            <AnimatedSection>
              <div className="bg-white rounded-2xl border border-[#E8E0D4] overflow-hidden">
                {/* Table Header */}
                <div className="hidden md:grid md:grid-cols-5 gap-4 p-4 bg-[#5B2D8E] text-white">
                  <div className="col-span-2 font-semibold">Ministry</div>
                  <div className="col-span-3 font-semibold">Mandate / Scope</div>
                </div>

                {/* Table Body */}
                <div className="divide-y divide-[#E8E0D4]">
                  {content.ministries.map((item, index) => (
                    <div 
                      key={index} 
                      className={`p-4 md:p-6 md:grid md:grid-cols-5 gap-4 ${
                        index === 0 ? 'bg-[#F5A623]/5' : index % 2 === 0 ? 'bg-[#F5F0E8]/50' : ''
                      }`}
                    >
                      <div className="col-span-2 mb-2 md:mb-0">
                        <span className={`font-semibold ${
                          index === 0 ? 'text-[#5B2D8E]' : 'text-[#2D2D2D]'
                        }`}>
                          {item.ministry}
                        </span>
                      </div>
                      <div className="col-span-3">
                        <span className="text-[#6B6B6B]">{item.mandate}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Footer Note */}
      <section className="py-12 bg-[#FFFEF9]">
        <div className="container mx-auto px-4 lg:px-10">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection>
              <div className="flex items-start gap-4 bg-[#F5F0E8] rounded-xl p-6 border border-[#E8E0D4]">
                <Info className="w-6 h-6 text-[#5B2D8E] flex-shrink-0 mt-0.5" />
                <p className="text-[#6B6B6B] italic">
                  {content.footer}
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ShadowCabinetPage;
